/**
 * Widget Mapping Service (DASH-20.3)
 * Maps tenant-specific IDs from source to target tenant by name matching
 *
 * SCHEMA VERIFIED 2025-11-12:
 * - Pipeline IDs: bigint
 * - Stage IDs: bigint
 * - Checklist item IDs: bigint (checklist_items.id)
 * - All tables have tenant_id (UUID) for isolation
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  MappingResult,
  PipelineMappingResult,
  StageMappingResult,
  ChecklistItemMappingResult,
  UnmappedPipeline,
  UnmappedStage,
  UnmappedChecklistItem,
  CompatibilityIssue,
  DashboardWidget,
} from '@/types/dashboard';

export class WidgetMappingService {
  /**
   * Main entry point: Map all widget configs in a shared dashboard
   *
   * @param dashboardId - ID of the newly created dashboard in target tenant
   * @param sourceTenantId - Source tenant UUID
   * @param targetTenantId - Target tenant UUID
   * @returns MappingResult with all mapped/unmapped IDs
   */
  static async mapDashboardWidgets(
    dashboardId: string,
    sourceTenantId: string,
    targetTenantId: string
  ): Promise<MappingResult> {
    try {
      console.log(`[WidgetMapping] Starting mapping for dashboard ${dashboardId}`);
      console.log(`[WidgetMapping] Source tenant: ${sourceTenantId}, Target tenant: ${targetTenantId}`);

      // Fetch all widgets for this dashboard
      const { data: widgets, error: widgetsError } = await supabase
        .from('dashboard_widgets')
        .select('id, config, dynamic_config')
        .eq('dashboard_id', dashboardId);

      if (widgetsError) {
        console.error('[WidgetMapping] Failed to fetch widgets:', widgetsError);
        throw widgetsError;
      }

      if (!widgets || widgets.length === 0) {
        console.log('[WidgetMapping] No widgets to map');
        return {
          dashboardId,
          widgetsProcessed: 0,
          pipelines: { mapped: {}, unmapped: [] },
          stages: { mapped: {}, unmapped: [] },
          checklistItems: { mapped: {}, unmapped: [] },
          users: { cleared: [] },
          teams: { cleared: [] },
        };
      }

      // Extract all IDs from all widgets
      const allPipelineIds = new Set<number>();
      const allStageIds = new Set<number>();
      const allChecklistItemIds = new Set<number>();
      const allUserIds = new Set<string>();
      const allTeamIds = new Set<string>();

      for (const widget of widgets) {
        const config = widget.config || {};
        const dynamicConfig = widget.dynamic_config || {};

        // Extract pipeline IDs (FIXED: config.filters.pipelineIds, not config.pipelineIds)
        if (config.filters?.pipelineIds && Array.isArray(config.filters.pipelineIds)) {
          config.filters.pipelineIds.forEach((id: number) => allPipelineIds.add(id));
        }
        if (dynamicConfig.filters?.pipelineIds && Array.isArray(dynamicConfig.filters.pipelineIds)) {
          dynamicConfig.filters.pipelineIds.forEach((id: number) => allPipelineIds.add(id));
        }

        // Extract stage IDs (FIXED: config.filters.stageIds for hiddenStageIds)
        if (config.filters?.stageIds && Array.isArray(config.filters.stageIds)) {
          config.filters.stageIds.forEach((id: number) => allStageIds.add(id));
        }
        if (dynamicConfig.filters?.stageIds && Array.isArray(dynamicConfig.filters.stageIds)) {
          dynamicConfig.filters.stageIds.forEach((id: number) => allStageIds.add(id));
        }

        // Extract checklist item IDs from projectFields filters (FIXED: config.filters.projectFields)
        if (config.filters?.projectFields && Array.isArray(config.filters.projectFields)) {
          config.filters.projectFields.forEach((filter: any) => {
            if (filter.source === 'checklist_item' && filter.checklistItemId) {
              allChecklistItemIds.add(Number(filter.checklistItemId));
            }
          });
        }
        if (dynamicConfig.filters?.projectFields && Array.isArray(dynamicConfig.filters.projectFields)) {
          dynamicConfig.filters.projectFields.forEach((filter: any) => {
            if (filter.source === 'checklist_item' && filter.checklistItemId) {
              allChecklistItemIds.add(Number(filter.checklistItemId));
            }
          });
        }

        // Extract user IDs (FIXED: config.filters.assignedUserIds)
        if (config.filters?.assignedUserIds && Array.isArray(config.filters.assignedUserIds)) {
          config.filters.assignedUserIds.forEach((id: string) => allUserIds.add(id));
        }
        if (dynamicConfig.filters?.assignedUserIds && Array.isArray(dynamicConfig.filters.assignedUserIds)) {
          dynamicConfig.filters.assignedUserIds.forEach((id: string) => allUserIds.add(id));
        }

        // Extract team IDs (FIXED: config.filters.teamIds)
        if (config.filters?.teamIds && Array.isArray(config.filters.teamIds)) {
          config.filters.teamIds.forEach((id: string) => allTeamIds.add(id));
        }
        if (dynamicConfig.filters?.teamIds && Array.isArray(dynamicConfig.filters.teamIds)) {
          dynamicConfig.filters.teamIds.forEach((id: string) => allTeamIds.add(id));
        }
      }

      console.log(`[WidgetMapping] Extracted IDs - Pipelines: ${allPipelineIds.size}, Stages: ${allStageIds.size}, Checklist Items: ${allChecklistItemIds.size}, Users: ${allUserIds.size}, Teams: ${allTeamIds.size}`);

      // Perform mappings
      const pipelineMapping = await this.mapPipelineIds(
        Array.from(allPipelineIds),
        sourceTenantId,
        targetTenantId
      );

      const stageMapping = await this.mapStageIds(
        Array.from(allStageIds),
        pipelineMapping.mapped,
        sourceTenantId,
        targetTenantId
      );

      const checklistItemMapping = await this.mapChecklistItemIds(
        Array.from(allChecklistItemIds),
        sourceTenantId,
        targetTenantId
      );

      // Users and teams are always cleared (don't attempt mapping)
      const clearedUsers = Array.from(allUserIds);
      const clearedTeams = Array.from(allTeamIds);

      // Update widget configs with mapped IDs
      for (const widget of widgets) {
        await this.updateWidgetConfig(
          widget,
          pipelineMapping,
          stageMapping,
          checklistItemMapping,
          clearedUsers,
          clearedTeams
        );
      }

      console.log(`[WidgetMapping] Mapping complete - ${widgets.length} widgets processed`);

      return {
        dashboardId,
        widgetsProcessed: widgets.length,
        pipelines: pipelineMapping,
        stages: stageMapping,
        checklistItems: checklistItemMapping,
        users: { cleared: clearedUsers },
        teams: { cleared: clearedTeams },
      };
    } catch (error) {
      console.error('[WidgetMapping] Mapping failed:', error);
      throw new Error('Failed to map widget configurations. Please check the logs.');
    }
  }

  /**
   * Pipeline mapping: Exact name match (case-insensitive)
   * Uses: pipelines table (id: bigint, name: text, tenant_id: uuid)
   */
  public static async mapPipelineIds(
    sourcePipelineIds: number[],
    sourceTenantId: string,
    targetTenantId: string
  ): Promise<PipelineMappingResult> {
    if (sourcePipelineIds.length === 0) {
      return { mapped: {}, unmapped: [] };
    }

    try {
      console.log(`[WidgetMapping] Mapping ${sourcePipelineIds.length} pipeline IDs`);

      // Fetch source pipelines by IDs (with tenant isolation)
      const { data: sourcePipelines, error: sourceError } = await supabase
        .from('pipelines')
        .select('id, name')
        .eq('tenant_id', sourceTenantId)
        .in('id', sourcePipelineIds);

      if (sourceError) throw sourceError;

      if (!sourcePipelines || sourcePipelines.length === 0) {
        console.warn('[WidgetMapping] No source pipelines found');
        return { mapped: {}, unmapped: [] };
      }

      // Fetch all target tenant pipelines
      const { data: targetPipelines, error: targetError } = await supabase
        .from('pipelines')
        .select('id, name')
        .eq('tenant_id', targetTenantId);

      if (targetError) throw targetError;

      if (!targetPipelines || targetPipelines.length === 0) {
        console.warn('[WidgetMapping] No target pipelines found');
        // All source pipelines become unmapped
        return {
          mapped: {},
          unmapped: sourcePipelines.map((p) => ({
            id: p.id,
            name: p.name,
            reason: 'no_matching_name' as const,
          })),
        };
      }

      // Map by exact name match (case-insensitive)
      const mapped: Record<number, number> = {};
      const unmapped: UnmappedPipeline[] = [];

      for (const sourcePipeline of sourcePipelines) {
        const matches = targetPipelines.filter(
          (t) => t.name.toLowerCase() === sourcePipeline.name.toLowerCase()
        );

        if (matches.length === 1) {
          // ✅ Exact match
          mapped[sourcePipeline.id] = matches[0].id;
          console.log(`[WidgetMapping] Mapped pipeline: ${sourcePipeline.name} (${sourcePipeline.id} → ${matches[0].id})`);
        } else if (matches.length === 0) {
          // ❌ No match
          unmapped.push({
            id: sourcePipeline.id,
            name: sourcePipeline.name,
            reason: 'no_matching_name',
          });
          console.warn(`[WidgetMapping] No match for pipeline: ${sourcePipeline.name} (${sourcePipeline.id})`);
        } else {
          // ❌ Multiple matches (ambiguous)
          unmapped.push({
            id: sourcePipeline.id,
            name: sourcePipeline.name,
            reason: 'multiple_matches',
          });
          console.warn(`[WidgetMapping] Multiple matches for pipeline: ${sourcePipeline.name} (${sourcePipeline.id})`);
        }
      }

      console.log(`[WidgetMapping] Pipeline mapping complete: ${Object.keys(mapped).length} mapped, ${unmapped.length} unmapped`);

      return { mapped, unmapped };
    } catch (error) {
      console.error('[WidgetMapping] Pipeline mapping failed:', error);
      throw error;
    }
  }

  /**
   * Stage mapping: Name match within pipeline context
   * Uses: stages table (id: bigint, name: text, pipeline_id: bigint, tenant_id: uuid)
   * Requires pipeline to be successfully mapped first
   */
  public static async mapStageIds(
    sourceStageIds: number[],
    pipelineMapping: Record<number, number>,
    sourceTenantId: string,
    targetTenantId: string
  ): Promise<StageMappingResult> {
    if (sourceStageIds.length === 0) {
      return { mapped: {}, unmapped: [] };
    }

    try {
      console.log(`[WidgetMapping] Mapping ${sourceStageIds.length} stage IDs`);

      // Fetch source stages WITH pipeline context
      const { data: sourceStages, error: sourceError } = await supabase
        .from('stages')
        .select(`
          id,
          name,
          pipeline_id,
          tenant_id,
          pipelines!inner(name)
        `)
        .eq('tenant_id', sourceTenantId)
        .in('id', sourceStageIds);

      if (sourceError) throw sourceError;

      if (!sourceStages || sourceStages.length === 0) {
        console.warn('[WidgetMapping] No source stages found');
        return { mapped: {}, unmapped: [] };
      }

      const mapped: Record<number, number> = {};
      const unmapped: UnmappedStage[] = [];

      for (const sourceStage of sourceStages) {
        const pipelineName = (sourceStage.pipelines as any)?.name || 'Unknown Pipeline';

        // Check if parent pipeline was mapped
        const targetPipelineId = pipelineMapping[sourceStage.pipeline_id];

        if (!targetPipelineId) {
          // ❌ Parent pipeline not mapped → Can't map stage
          unmapped.push({
            id: sourceStage.id,
            name: sourceStage.name,
            pipelineName: pipelineName,
            reason: 'parent_pipeline_not_mapped',
          });
          console.warn(`[WidgetMapping] Parent pipeline not mapped for stage: ${sourceStage.name} (${sourceStage.id}) in pipeline ${pipelineName}`);
          continue;
        }

        // Look for stage with same name in target pipeline
        const { data: targetStages, error: targetError } = await supabase
          .from('stages')
          .select('id, name')
          .eq('pipeline_id', targetPipelineId)
          .eq('tenant_id', targetTenantId)
          .ilike('name', sourceStage.name); // Case-insensitive match

        if (targetError) throw targetError;

        if (targetStages && targetStages.length === 1) {
          // ✅ Found matching stage in mapped pipeline
          mapped[sourceStage.id] = targetStages[0].id;
          console.log(`[WidgetMapping] Mapped stage: ${sourceStage.name} (${sourceStage.id} → ${targetStages[0].id}) in pipeline ${pipelineName}`);
        } else if (!targetStages || targetStages.length === 0) {
          // ❌ Stage doesn't exist in target pipeline
          unmapped.push({
            id: sourceStage.id,
            name: sourceStage.name,
            pipelineName: pipelineName,
            reason: 'stage_not_found_in_pipeline',
          });
          console.warn(`[WidgetMapping] Stage not found in target pipeline: ${sourceStage.name} (${sourceStage.id}) in pipeline ${pipelineName}`);
        } else {
          // ❌ Multiple stages with same name (ambiguous)
          unmapped.push({
            id: sourceStage.id,
            name: sourceStage.name,
            pipelineName: pipelineName,
            reason: 'no_matching_name',
          });
          console.warn(`[WidgetMapping] Multiple stages found for: ${sourceStage.name} (${sourceStage.id}) in pipeline ${pipelineName}`);
        }
      }

      console.log(`[WidgetMapping] Stage mapping complete: ${Object.keys(mapped).length} mapped, ${unmapped.length} unmapped`);

      return { mapped, unmapped };
    } catch (error) {
      console.error('[WidgetMapping] Stage mapping failed:', error);
      throw error;
    }
  }

  /**
   * Checklist item mapping: Template name + item name + item type
   * Uses:
   * - checklist_templates table (id: bigint, name: text, tenant_id: uuid)
   * - checklist_items table (id: bigint, template_id: bigint, item_name: text, item_type: text, tenant_id: uuid)
   * All 3 must match for successful mapping
   */
  public static async mapChecklistItemIds(
    sourceItemIds: number[],
    sourceTenantId: string,
    targetTenantId: string
  ): Promise<ChecklistItemMappingResult> {
    if (sourceItemIds.length === 0) {
      return { mapped: {}, unmapped: [] };
    }

    try {
      console.log(`[WidgetMapping] Mapping ${sourceItemIds.length} checklist item IDs`);

      // Fetch source checklist items WITH template context
      const { data: sourceItems, error: sourceError } = await supabase
        .from('checklist_items')
        .select(`
          id,
          item_name,
          item_type,
          template_id,
          checklist_templates!inner(id, name)
        `)
        .eq('tenant_id', sourceTenantId)
        .in('id', sourceItemIds);

      if (sourceError) throw sourceError;

      if (!sourceItems || sourceItems.length === 0) {
        console.warn('[WidgetMapping] No source checklist items found');
        return { mapped: {}, unmapped: [] };
      }

      const mapped: Record<number, number> = {};
      const unmapped: UnmappedChecklistItem[] = [];

      for (const sourceItem of sourceItems) {
        const templateName = (sourceItem.checklist_templates as any)?.name || 'Unknown Template';

        // Find matching template in target tenant by name
        const { data: targetTemplates, error: templateError } = await supabase
          .from('checklist_templates')
          .select('id, name')
          .eq('tenant_id', targetTenantId)
          .ilike('name', templateName);

        if (templateError) throw templateError;

        if (!targetTemplates || targetTemplates.length === 0) {
          // ❌ Template doesn't exist in target tenant
          unmapped.push({
            id: sourceItem.id,
            name: sourceItem.item_name,
            type: sourceItem.item_type,
            templateName: templateName,
            reason: 'template_not_found',
          });
          console.warn(`[WidgetMapping] Template not found: ${templateName} for item ${sourceItem.item_name} (${sourceItem.id})`);
          continue;
        }

        if (targetTemplates.length > 1) {
          // ❌ Multiple templates with same name (ambiguous)
          unmapped.push({
            id: sourceItem.id,
            name: sourceItem.item_name,
            type: sourceItem.item_type,
            templateName: templateName,
            reason: 'item_not_found',
          });
          console.warn(`[WidgetMapping] Multiple templates found: ${templateName} for item ${sourceItem.item_name} (${sourceItem.id})`);
          continue;
        }

        const targetTemplate = targetTemplates[0];

        // Find matching checklist item by name + type within template
        const { data: targetItems, error: itemError } = await supabase
          .from('checklist_items')
          .select('id, item_name, item_type')
          .eq('template_id', targetTemplate.id)
          .eq('tenant_id', targetTenantId)
          .ilike('item_name', sourceItem.item_name)
          .eq('item_type', sourceItem.item_type); // Type must match exactly

        if (itemError) throw itemError;

        if (targetItems && targetItems.length === 1) {
          // ✅ Found exact match (template + name + type)
          mapped[sourceItem.id] = targetItems[0].id;
          console.log(`[WidgetMapping] Mapped checklist item: ${sourceItem.item_name} (${sourceItem.id} → ${targetItems[0].id}) in template ${templateName}`);
        } else if (!targetItems || targetItems.length === 0) {
          // ❌ Item not found or type mismatch
          unmapped.push({
            id: sourceItem.id,
            name: sourceItem.item_name,
            type: sourceItem.item_type,
            templateName: templateName,
            reason: 'item_not_found',
          });
          console.warn(`[WidgetMapping] Item not found: ${sourceItem.item_name} (type: ${sourceItem.item_type}) (${sourceItem.id}) in template ${templateName}`);
        } else {
          // ❌ Multiple items with same name (shouldn't happen, but handle it)
          unmapped.push({
            id: sourceItem.id,
            name: sourceItem.item_name,
            type: sourceItem.item_type,
            templateName: templateName,
            reason: 'item_not_found',
          });
          console.warn(`[WidgetMapping] Multiple items found for: ${sourceItem.item_name} (${sourceItem.id}) in template ${templateName}`);
        }
      }

      console.log(`[WidgetMapping] Checklist item mapping complete: ${Object.keys(mapped).length} mapped, ${unmapped.length} unmapped`);

      return { mapped, unmapped };
    } catch (error) {
      console.error('[WidgetMapping] Checklist item mapping failed:', error);
      throw error;
    }
  }

  /**
   * Update widget config with mapped IDs
   * Replaces mapped IDs and adds _needsConfiguration flag if any unmapped
   */
  private static async updateWidgetConfig(
    widget: Pick<DashboardWidget, 'id' | 'config' | 'dynamic_config'>,
    pipelineMapping: PipelineMappingResult,
    stageMapping: StageMappingResult,
    checklistItemMapping: ChecklistItemMappingResult,
    clearedUsers: string[],
    clearedTeams: string[]
  ): Promise<void> {
    try {
      const config = { ...(widget.config || {}) };
      const dynamicConfig = { ...(widget.dynamic_config || {}) };
      let needsConfiguration = false;
      const unmappedReferences: any = {
        pipelines: [],
        stages: [],
        checklistItems: [],
        users: [],
        teams: [],
      };

      // Map pipeline IDs in config (FIXED: config.filters.pipelineIds)
      if (config.filters?.pipelineIds && Array.isArray(config.filters.pipelineIds)) {
        const { mapped, unmapped } = this.mapArrayIds(
          config.filters.pipelineIds,
          pipelineMapping.mapped
        );
        config.filters.pipelineIds = mapped;
        if (unmapped.length > 0) {
          needsConfiguration = true;
          unmappedReferences.pipelines.push(...unmapped.map((id) => {
            const unmappedInfo = pipelineMapping.unmapped.find((u) => u.id === id);
            return { id, originalName: unmappedInfo?.name || 'Unknown' };
          }));
        }
      }

      // Map pipeline IDs in dynamic config
      if (dynamicConfig.filters?.pipelineIds && Array.isArray(dynamicConfig.filters.pipelineIds)) {
        const { mapped, unmapped } = this.mapArrayIds(
          dynamicConfig.filters.pipelineIds,
          pipelineMapping.mapped
        );
        dynamicConfig.filters.pipelineIds = mapped;
        if (unmapped.length > 0) {
          needsConfiguration = true;
          unmappedReferences.pipelines.push(...unmapped.map((id) => {
            const unmappedInfo = pipelineMapping.unmapped.find((u) => u.id === id);
            return { id, originalName: unmappedInfo?.name || 'Unknown' };
          }));
        }
      }

      // Map stage IDs (FIXED: config.filters.stageIds for hiddenStageIds)
      if (config.filters?.stageIds && Array.isArray(config.filters.stageIds)) {
        const { mapped, unmapped } = this.mapArrayIds(
          config.filters.stageIds,
          stageMapping.mapped
        );
        config.filters.stageIds = mapped;
        if (unmapped.length > 0) {
          needsConfiguration = true;
          unmappedReferences.stages.push(...unmapped.map((id) => {
            const unmappedInfo = stageMapping.unmapped.find((u) => u.id === id);
            return { id, originalName: unmappedInfo?.name || 'Unknown', pipelineName: unmappedInfo?.pipelineName || 'Unknown' };
          }));
        }
      }

      // Map stage IDs in dynamic config
      if (dynamicConfig.filters?.stageIds && Array.isArray(dynamicConfig.filters.stageIds)) {
        const { mapped, unmapped } = this.mapArrayIds(
          dynamicConfig.filters.stageIds,
          stageMapping.mapped
        );
        dynamicConfig.filters.stageIds = mapped;
        if (unmapped.length > 0) {
          needsConfiguration = true;
          unmappedReferences.stages.push(...unmapped.map((id) => {
            const unmappedInfo = stageMapping.unmapped.find((u) => u.id === id);
            return { id, originalName: unmappedInfo?.name || 'Unknown', pipelineName: unmappedInfo?.pipelineName || 'Unknown' };
          }));
        }
      }

      // Map checklist item IDs in projectFields (FIXED: config.filters.projectFields)
      if (config.filters?.projectFields && Array.isArray(config.filters.projectFields)) {
        config.filters.projectFields = config.filters.projectFields.map((filter: any) => {
          if (filter.source === 'checklist_item' && filter.checklistItemId) {
            const itemId = Number(filter.checklistItemId);
            const mappedId = checklistItemMapping.mapped[itemId];
            if (mappedId) {
              return { ...filter, checklistItemId: String(mappedId) };
            } else {
              needsConfiguration = true;
              const unmappedInfo = checklistItemMapping.unmapped.find((u) => u.id === itemId);
              unmappedReferences.checklistItems.push({
                id: itemId,
                originalName: unmappedInfo?.name || 'Unknown',
                type: unmappedInfo?.type || 'Unknown',
                templateName: unmappedInfo?.templateName || 'Unknown',
              });
              return filter; // Keep unmapped filter
            }
          }
          return filter;
        });
      }

      // Map checklist item IDs in dynamic config projectFields
      if (dynamicConfig.filters?.projectFields && Array.isArray(dynamicConfig.filters.projectFields)) {
        dynamicConfig.filters.projectFields = dynamicConfig.filters.projectFields.map((filter: any) => {
          if (filter.source === 'checklist_item' && filter.checklistItemId) {
            const itemId = Number(filter.checklistItemId);
            const mappedId = checklistItemMapping.mapped[itemId];
            if (mappedId) {
              return { ...filter, checklistItemId: String(mappedId) };
            } else {
              needsConfiguration = true;
              const unmappedInfo = checklistItemMapping.unmapped.find((u) => u.id === itemId);
              unmappedReferences.checklistItems.push({
                id: itemId,
                originalName: unmappedInfo?.name || 'Unknown',
                type: unmappedInfo?.type || 'Unknown',
                templateName: unmappedInfo?.templateName || 'Unknown',
              });
              return filter; // Keep unmapped filter
            }
          }
          return filter;
        });
      }

      // Clear user IDs (FIXED: config.filters.assignedUserIds)
      if (config.filters?.assignedUserIds && Array.isArray(config.filters.assignedUserIds) && config.filters.assignedUserIds.length > 0) {
        config.filters.assignedUserIds = [];
        needsConfiguration = true;
        unmappedReferences.users.push(...clearedUsers);
      }

      if (dynamicConfig.filters?.assignedUserIds && Array.isArray(dynamicConfig.filters.assignedUserIds) && dynamicConfig.filters.assignedUserIds.length > 0) {
        dynamicConfig.filters.assignedUserIds = [];
        needsConfiguration = true;
        unmappedReferences.users.push(...clearedUsers);
      }

      // Clear team IDs (FIXED: config.filters.teamIds)
      if (config.filters?.teamIds && Array.isArray(config.filters.teamIds) && config.filters.teamIds.length > 0) {
        config.filters.teamIds = [];
        needsConfiguration = true;
        unmappedReferences.teams.push(...clearedTeams);
      }

      if (dynamicConfig.filters?.teamIds && Array.isArray(dynamicConfig.filters.teamIds) && dynamicConfig.filters.teamIds.length > 0) {
        dynamicConfig.filters.teamIds = [];
        needsConfiguration = true;
        unmappedReferences.teams.push(...clearedTeams);
      }

      // Add compatibility metadata
      if (needsConfiguration) {
        config._needsConfiguration = true;
        config._unmappedReferences = unmappedReferences;
      }

      // Update widget config in database
      const { error: updateError } = await supabase
        .from('dashboard_widgets')
        .update({
          config: config,
          dynamic_config: dynamicConfig,
        })
        .eq('id', widget.id);

      if (updateError) {
        console.error(`[WidgetMapping] Failed to update widget ${widget.id}:`, updateError);
        throw updateError;
      }

      console.log(`[WidgetMapping] Updated widget ${widget.id} (needsConfiguration: ${needsConfiguration})`);
    } catch (error) {
      console.error('[WidgetMapping] Failed to update widget config:', error);
      throw error;
    }
  }

  /**
   * Helper: Map array of IDs, returning mapped and unmapped
   */
  private static mapArrayIds(
    sourceIds: number[],
    mapping: Record<number, number>
  ): { mapped: number[]; unmapped: number[] } {
    const mapped: number[] = [];
    const unmapped: number[] = [];

    for (const id of sourceIds) {
      const mappedId = mapping[id];
      if (mappedId !== undefined) {
        mapped.push(mappedId);
      } else {
        unmapped.push(id);
      }
    }

    return { mapped, unmapped };
  }
}
